import { Product } from '@diamond/shared/types';
import { getUniqueArray } from '@diamond/shared/utils';
import { useQuery } from '@tanstack/react-query';
import shallow from 'zustand/shallow';

import { getProductList } from '../queries';
import { useAuthStore, useShipToStore } from '../stores';
import {
  PRODUCT_RECOMMENDATIONS_KEY,
  useProductRecommendations,
} from './useProductRecommendations';

type ProductAutocompleteSource = 'recommendation' | 'product-list' | 'none';

type ProductAutocompleteResult = {
  products: string[];
  brands: string[];
  source: ProductAutocompleteSource;
  isLoading: boolean;
  isEmpty: boolean;
};

const DEFAULT_PRODUCT_LIST_SIZE = 10;

export const PRODUCT_AUTOCOMPLETE_KEY = 'ProductAutocomplete';

export const useProductAutocomplete = (
  keyword: string,
  options?: {
    size?: number;
  }
): ProductAutocompleteResult => {
  const session = useAuthStore();
  const [selectedShipToId] = useShipToStore(
    (s) => [s.selectedShipToId],
    shallow
  );

  const trimmedKeyword = keyword.trim();
  const productListSize = options?.size ?? DEFAULT_PRODUCT_LIST_SIZE;

  const recommendationQuery = useProductRecommendations(trimmedKeyword);

  const isRecommendationEmpty = Boolean(
    recommendationQuery.data &&
      recommendationQuery.data.products.length === 0 &&
      recommendationQuery.data.brands.length === 0
  );

  const hasRecommendationData =
    Boolean(recommendationQuery.data) &&
    (recommendationQuery.data.products.length > 0 ||
      recommendationQuery.data.brands.length > 0);

  const shouldFetchProductList =
    trimmedKeyword !== '' &&
    recommendationQuery.isSuccess &&
    isRecommendationEmpty &&
    !hasRecommendationData;

  const productListQuery = useQuery<Product[]>({
    queryKey: [
      PRODUCT_AUTOCOMPLETE_KEY,
      'product-list',
      {
        keyword: trimmedKeyword,
        warehouse: selectedShipToId,
        size: productListSize,
      },
    ],
    queryFn: () =>
      getProductList(session.access_token, {
        search_query: trimmedKeyword,
        warehouse: selectedShipToId,
        page: 1,
        size: productListSize,
      }),
    enabled: shouldFetchProductList,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const productListTitles = getUniqueArray(
    (productListQuery.data ?? [])
      .map((product) => product.title)
      .filter((title): title is string => Boolean(title))
  );

  const productListBrands = getUniqueArray(
    (productListQuery.data ?? [])
      .map((product) => product.brand_desc || product.brand_name)
      .filter((brand): brand is string => Boolean(brand))
  );

  const hasProductListData =
    productListTitles.length > 0 || productListBrands.length > 0;

  const products = hasRecommendationData
    ? getUniqueArray(recommendationQuery.data?.products ?? [])
    : hasProductListData
    ? productListTitles
    : [];

  const brands = hasRecommendationData
    ? getUniqueArray(recommendationQuery.data?.brands ?? [])
    : hasProductListData
    ? productListBrands
    : [];

  let source: ProductAutocompleteSource = 'none';
  if (hasRecommendationData) {
    source = 'recommendation';
  } else if (hasProductListData) {
    source = 'product-list';
  }

  const isLoadingPrimary = recommendationQuery.isPending;
  const isLoadingProductList =
    shouldFetchProductList && productListQuery.isPending;
  const isFetchingWithoutData =
    (!hasRecommendationData && recommendationQuery.isFetching) ||
    (!hasProductListData && productListQuery.isFetching);

  const isLoading =
    isLoadingPrimary || isLoadingProductList || isFetchingWithoutData;

  const isEmpty =
    !isLoading &&
    !hasRecommendationData &&
    !hasProductListData &&
    trimmedKeyword !== '';

  return {
    products,
    brands,
    source,
    isLoading,
    isEmpty,
  };
};
